import React, { useState } from 'react';
import { useTechAuth } from '../../../contexts/TechAuthContext';
import Icon from '../../../components/AppIcon';

const SPECIALIZATIONS = [
  'CT Scan', 'MRI', 'X-Ray / Radiography', 'Ultrasound', 'Nuclear Medicine',
  'Mammography', 'Fluoroscopy', 'PET Scan', 'Interventional Radiology', 'General Imaging',
];

export default function TechRegisterForm({ onSwitchToLogin }) {
  const { register } = useTechAuth();
  const [form, setForm] = useState({ full_name: '', email: '', password: '', confirm: '', facility: '', specialization: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e?.target?.value }));

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    if (form?.password !== form?.confirm) { setError('Passwords do not match.'); return; }
    if (form?.password?.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setLoading(true);
    try {
      await register(form?.email, form?.password, {
        full_name: form?.full_name,
        facility: form?.facility,
        specialization: form?.specialization,
      });
      setSuccess(true);
    } catch (err) {
      setError(err?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="text-center py-6 space-y-4">
        <div className="w-14 h-14 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto">
          <Icon name="CheckCircle" size={28} className="text-green-600" />
        </div>
        <h3 className="text-lg font-semibold text-foreground">Account Created!</h3>
        <p className="text-sm text-muted-foreground">Check your email to confirm your account, then sign in.</p>
        <button onClick={onSwitchToLogin} className="text-primary font-medium text-sm hover:underline">
          Back to Sign In
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="flex items-start gap-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <Icon name="AlertCircle" size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}
      {[
        { key: 'full_name', label: 'Full Name', icon: 'User', type: 'text', placeholder: 'Jane Smith' },
        { key: 'email', label: 'Email Address', icon: 'Mail', type: 'email', placeholder: 'you@facility.com' },
        { key: 'facility', label: 'Facility / Hospital', icon: 'Building2', type: 'text', placeholder: 'City Medical Imaging Centre' },
      ]?.map(({ key, label, icon, type, placeholder }) => (
        <div key={key}>
          <label className="block text-sm font-medium text-foreground mb-1.5">{label}</label>
          <div className="relative">
            <Icon name={icon} size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type={type}
              value={form?.[key]}
              onChange={set(key)}
              required
              placeholder={placeholder}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 text-sm"
            />
          </div>
        </div>
      ))}
      <div>
        <label className="block text-sm font-medium text-foreground mb-1.5">Specialization</label>
        <div className="relative">
          <Icon name="Stethoscope" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <select
            value={form?.specialization}
            onChange={set('specialization')}
            required
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 text-sm appearance-none"
          >
            <option value="">Select specialization…</option>
            {SPECIALIZATIONS?.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
      {[
        { key: 'password', label: 'Password', placeholder: '••••••••' },
        { key: 'confirm', label: 'Confirm Password', placeholder: '••••••••' },
      ]?.map(({ key, label, placeholder }) => (
        <div key={key}>
          <label className="block text-sm font-medium text-foreground mb-1.5">{label}</label>
          <div className="relative">
            <Icon name="Lock" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="password"
              value={form?.[key]}
              onChange={set(key)}
              required
              placeholder={placeholder}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 text-sm"
            />
          </div>
        </div>
      ))}
      <button
        type="submit"
        disabled={loading}
        className="w-full py-2.5 px-4 bg-primary text-primary-foreground rounded-lg font-medium text-sm hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
            Creating account…
          </>
        ) : 'Create Account'}
      </button>
      <p className="text-center text-sm text-muted-foreground">
        Already have an account?{' '}
        <button type="button" onClick={onSwitchToLogin} className="text-primary font-medium hover:underline">
          Sign in
        </button>
      </p>
    </form>
  );
}
